import * as THREE from 'three'

// ─────────────────────────────────────────────────────────────────────────────
// Geographic Atlanta board geometry.
//
// The game engine stays the source of truth: it only knows a path index and a
// tile index. This module turns that into world coordinates along a road map
// modeled on Atlanta's real corridor relationships — the Downtown Connector
// (I-75/I-85), I-20 east/west, the I-285 Perimeter loop, GA-400 north, and
// Peachtree running diagonally through the core.
//
// Every player route passes through the Downtown interchange at a DIFFERENT
// elevation, which is what produces real crossovers instead of parallel lanes.
// ─────────────────────────────────────────────────────────────────────────────

export const LANE_COUNT = 4
export const LANE_WIDTH = 5
export const BOARD_RADIUS = 104

/** Route accent color per life path. */
export const LANE_NEON: string[] = ['#a855f7', '#ec4899', '#fb923c', '#2dd4bf']

export const PLAYER_HEX: Record<string, string> = {
  pink: '#ec4899',
  purple: '#a855f7',
  blue: '#3b82f6',
  teal: '#14b8a6',
  gold: '#f59e0b',
  coral: '#fb7185',
}

export const CATEGORY_HEX: Record<string, string> = {
  start: '#ffffff',
  finish: '#fde047',
  heartbreak: '#fb7185',
  money: '#fbbf24',
  money_loss: '#ef4444',
  glowup: '#c084fc',
  chaos: '#f472b6',
  blessing: '#5eead4',
  wildcard: '#60a5fa',
  tax: '#f87171',
}

export const DEFAULT_CATEGORY_HEX = '#a78bfa'

export function categoryColor(category: string): string {
  return CATEGORY_HEX[category] ?? DEFAULT_CATEGORY_HEX
}

export function playerColor(color: string): string {
  return PLAYER_HEX[color] ?? '#f472b6'
}

export interface AtlantaRoute {
  id: string
  /** Human readable corridor description, used for map signage. */
  label: string
  /** Ordered [x, y, z] control points. -Z is north, +X is east. */
  points: Array<[number, number, number]>
}

/**
 * The four player corridors. Each one enters the Downtown interchange at its
 * own deck height so routes visibly stack and cross at the center of the map.
 */
export const ATLANTA_ROUTES: AtlantaRoute[] = [
  {
    id: 'ga400-connector',
    label: 'GA 400 → Downtown Connector',
    points: [
      [10, 0, -96],
      [10, 0, -78],
      [6, 0.4, -58],
      [0, 1.2, -42],
      [-6, 1.8, -34],
      [-4, 2.2, -18],
      [0, 2.6, 0],
      [2, 1.8, 12],
      [0, 0.8, 32],
      [2, 0.2, 54],
      [4, 0, 76],
    ],
  },
  {
    id: 'i85-i20west',
    label: 'I-85 North → Connector → I-20 West',
    points: [
      [66, 0, -86],
      [46, 0, -62],
      [28, 0.2, -44],
      [12, 0.2, -28],
      [4, 0, -12],
      [0, 0, 0],
      [-14, 0, 6],
      [-32, 0, 10],
      [-58, 0, 14],
      [-74, 0, -4],
    ],
  },
  {
    id: 'i20east-i75south',
    label: 'I-20 East → Downtown → I-75 South',
    points: [
      [90, 0, 12],
      [74, 0, 4],
      [60, 0.3, 6],
      [38, 0.8, 4],
      [18, 1.1, 2],
      [0, 1.3, 0],
      [-6, 1, 14],
      [-4, 0.4, 36],
      [0, 0, 58],
      [4, 0, 76],
    ],
  },
  {
    id: 'perimeter-peachtree',
    label: 'I-285 West → Peachtree → Downtown',
    points: [
      [-86, 0, -42],
      [-78, 0, -20],
      [-72, 0.4, -6],
      [-54, 1, -14],
      [-36, 1.6, -22],
      [-20, 2.6, -16],
      [-8, 3.4, -6],
      [0, 3.8, 0],
      [14, 3, 12],
      [30, 1.8, 26],
      [46, 0.6, 44],
      [58, 0, 62],
    ],
  },
]

function toVectors(points: Array<[number, number, number]>): THREE.Vector3[] {
  return points.map(([x, y, z]) => new THREE.Vector3(x, y, z))
}

/** Builds the drivable curve for one life path's Atlanta corridor. */
export function createRouteCurve(laneIndex: number): THREE.CatmullRomCurve3 {
  const route = ATLANTA_ROUTES[laneIndex] ?? ATLANTA_ROUTES[0]
  return new THREE.CatmullRomCurve3(
    toVectors(route.points),
    false,
    'catmullrom',
    0.4,
  )
}

/** The I-285 Perimeter: a closed loop framing the whole metro board. */
export function createPerimeterCurve(): THREE.CatmullRomCurve3 {
  const points: THREE.Vector3[] = []
  const radiusX = 88
  const radiusZ = 84
  const steps = 18
  for (let i = 0; i < steps; i++) {
    const angle = (i / steps) * Math.PI * 2
    // Slight radius jitter keeps the loop from reading as a perfect circle,
    // matching how the real Perimeter bulges around the metro.
    const wobble = 1 + Math.sin(angle * 3) * 0.045
    points.push(
      new THREE.Vector3(
        Math.cos(angle) * radiusX * wobble,
        0,
        Math.sin(angle) * radiusZ * wobble,
      ),
    )
  }
  return new THREE.CatmullRomCurve3(points, true, 'catmullrom', 0.5)
}

/** Peachtree Street: the diagonal surface spine through Midtown/Downtown. */
export function createPeachtreeCurve(): THREE.CatmullRomCurve3 {
  return new THREE.CatmullRomCurve3(
    toVectors([
      [-16, 0, -86],
      [-10, 0, -62],
      [-4, 0, -40],
      [1, 0, -18],
      [4, 0, 4],
      [6, 0, 28],
      [10, 0, 52],
      [16, 0, 74],
    ]),
    false,
    'catmullrom',
    0.5,
  )
}

export interface MapLabel {
  text: string
  x: number
  z: number
  /** 'interstate' renders a blue/red shield, 'state' a green route marker. */
  kind: 'interstate' | 'state' | 'place'
}

/** Freeway shields and place names that identify the map as Atlanta. */
export const MAP_LABELS: MapLabel[] = [
  { text: 'DOWNTOWN ATLANTA', x: 22, z: 2, kind: 'place' },
  { text: 'MIDTOWN', x: -22, z: -40, kind: 'place' },
  { text: '285', x: -2, z: -86, kind: 'interstate' },
  { text: '285', x: 4, z: 86, kind: 'interstate' },
  { text: '285', x: -88, z: 4, kind: 'interstate' },
  { text: '285', x: 90, z: -8, kind: 'interstate' },
  { text: '400', x: 12, z: -70, kind: 'state' },
  { text: '85', x: 52, z: -66, kind: 'interstate' },
  { text: '75', x: 6, z: 56, kind: 'interstate' },
  { text: '20', x: -50, z: 16, kind: 'interstate' },
  { text: '20', x: 62, z: 8, kind: 'interstate' },
]

/** Normalized 0..1 progress for a tile index within a path. */
export function tileProgress(index: number, tileCount: number): number {
  if (tileCount <= 1) return 0
  const u = index / (tileCount - 1)
  return Math.min(1, Math.max(0, u))
}

/**
 * Builds a flat road ribbon that follows a curve, including elevation changes
 * so overpasses and ramps read correctly where routes cross.
 */
export function createLaneRibbonGeometry(
  curve: THREE.Curve<THREE.Vector3>,
  width: number,
  segments = 90,
): THREE.BufferGeometry {
  const positions: number[] = []
  const normals: number[] = []
  const uvs: number[] = []
  const indices: number[] = []
  const up = new THREE.Vector3(0, 1, 0)
  const side = new THREE.Vector3()

  for (let i = 0; i <= segments; i++) {
    const u = i / segments
    const point = curve.getPointAt(u)
    const tangent = curve.getTangentAt(u)
    side.crossVectors(tangent, up).normalize().multiplyScalar(width / 2)

    positions.push(
      point.x - side.x,
      point.y,
      point.z - side.z,
      point.x + side.x,
      point.y,
      point.z + side.z,
    )
    normals.push(0, 1, 0, 0, 1, 0)
    uvs.push(0, u * 6, 1, u * 6)
  }

  for (let i = 0; i < segments; i++) {
    const a = i * 2
    const b = a + 1
    const c = a + 2
    const d = a + 3
    indices.push(a, b, c, b, d, c)
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3))
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
  geometry.setIndex(indices)
  geometry.computeBoundingSphere()
  return geometry
}

/** Samples every road so scenery can avoid being placed on the pavement. */
export function sampleCurvePoints(
  curves: Array<THREE.Curve<THREE.Vector3>>,
  perCurve = 70,
): THREE.Vector3[] {
  const points: THREE.Vector3[] = []
  curves.forEach((curve) => {
    for (let i = 0; i <= perCurve; i++) {
      points.push(curve.getPointAt(i / perCurve).clone())
    }
  })
  return points
}

/** True when a spot is far enough from every road to place scenery on it. */
export function isClearOfRoads(
  x: number,
  z: number,
  roadPoints: THREE.Vector3[],
  clearance: number,
): boolean {
  const clearanceSquared = clearance * clearance
  for (let i = 0; i < roadPoints.length; i++) {
    const point = roadPoints[i]
    const dx = point.x - x
    const dz = point.z - z
    if (dx * dx + dz * dz < clearanceSquared) return false
  }
  return true
}

/** A dusk sky gradient so the metro reads as an evening map. */
export function createSkyTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = 4
  canvas.height = 256
  const ctx = canvas.getContext('2d')
  if (ctx) {
    const gradient = ctx.createLinearGradient(0, 0, 0, 256)
    gradient.addColorStop(0, '#101a35')
    gradient.addColorStop(0.5, '#25264f')
    gradient.addColorStop(0.82, '#4a3260')
    gradient.addColorStop(1, '#161129')
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, 4, 256)
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

/** Draws an interstate shield / state route marker / place label texture. */
export function createLabelTexture(label: MapLabel): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 128
  const ctx = canvas.getContext('2d')

  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    if (label.kind === 'place') {
      ctx.fillStyle = 'rgba(12, 10, 26, 0.82)'
      ctx.fillRect(6, 34, 244, 60)
      ctx.strokeStyle = '#f9d67a'
      ctx.lineWidth = 4
      ctx.strokeRect(6, 34, 244, 60)
      ctx.fillStyle = '#ffe9ab'
      ctx.font = 'bold 30px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(label.text, 128, 64)
    } else if (label.kind === 'interstate') {
      ctx.fillStyle = '#12336b'
      ctx.beginPath()
      ctx.roundRect(72, 20, 112, 92, [30, 30, 44, 44])
      ctx.fill()
      ctx.fillStyle = '#b8202e'
      ctx.beginPath()
      ctx.roundRect(72, 20, 112, 26, [26, 26, 0, 0])
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 5
      ctx.beginPath()
      ctx.roundRect(72, 20, 112, 92, [30, 30, 44, 44])
      ctx.stroke()
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 52px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(label.text, 128, 76)
    } else {
      ctx.fillStyle = '#1d5c3a'
      ctx.beginPath()
      ctx.roundRect(74, 22, 108, 88, 16)
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 5
      ctx.beginPath()
      ctx.roundRect(74, 22, 108, 88, 16)
      ctx.stroke()
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 48px Arial'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(label.text, 128, 68)
    }
  }

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}
